import { useMemo, useState } from "react"
import LinkButton from "../../components/LinkButton"
import { createColumnHelper } from "@tanstack/react-table"
import IconWrapper from "../../components/IconWrapper"
import { useNavigate, useSearchParams } from "react-router-dom"
import routePaths from "../../constants/path"
import { useResultStatsForPatientIssuer } from "../../hooks/useOrderResultsStatistics"
import Loader from "../../components/Loader"
import ApiError from "../../components/ApiError"
import { formatISODate } from "../../utils/datetime"
import StatisticsTable from "./components/StatisticsTable"
import { dbDateFormat } from "../../constants"

const columnHelper = createColumnHelper()

const ResultsStatistics = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [sortingForResults, setSortingForResults] = useState([
    { id: "PatientIssuer", desc: false },
  ])
  const patientIssuer = searchParams.get("patientIssuer") ?? null
  const fromDate = searchParams.get("from") ?? null
  const toDate = searchParams.get("to") ?? null

  const fromISO = fromDate ? formatISODate(fromDate, dbDateFormat) : null
  const toISO = toDate ? formatISODate(toDate, dbDateFormat) : null

  const { data, isLoading, isError, error } = useResultStatsForPatientIssuer({
    patientIssuer,
    from: fromISO,
    to: toISO,
  })

  const goBack = () => navigate(-1)

  const resultTableColumns = useMemo(
    () => [
      columnHelper.accessor("InboundMessageId", {
        header: "Result ID",
        id: "InboundMessageId",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("AccessionNo", {
        header: "Accession No",
        id: "AccessionNo",
        cell: (info) => (
          <LinkButton
            onClick={() => {
              navigate(
                `${routePaths.orderTracking}?patientIssuer=${patientIssuer}&accessionNumber=${info.renderValue()}`
              )
            }}
          >
            {info.renderValue()}
          </LinkButton>
        ),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("ResultStatus", {
        header: "Result Status",
        id: "resultStatus",
        cell: (info) => info.renderValue() ?? "-",
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("resultSent", {
        header: "Result Sent",
        id: "resultSent",
        cell: (info) =>
          info.renderValue()
            ? formatISODate(
                info.renderValue().replace("Z", ""),
                "MM/dd/yyyy hh:mm:ss a"
              )
            : "-",
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
    ],
    [navigate, patientIssuer]
  )

  return (
    <div className="container mx-auto relative my-8 w-full md:w-4/5 lg:w-3/4 px-4">
      {/* Information Card */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="flex gap-3 items-center bg-white rounded-t-xl shadow-md px-6 py-4 sticky top-0 mb-2 border-b border-gray-200">
            <IconWrapper onClick={goBack}>
              <span className="material-icons">arrow_back</span>
            </IconWrapper>
            <h1 className="text-2xl font-bold text-primary">
              Results Statistics
            </h1>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500 uppercase mb-1">
                Patient Issuer
              </div>
              <div className="text-lg font-semibold text-primary">
                {patientIssuer ?? "N/A"}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500 uppercase mb-1">
                From Date
              </div>
              <div className="text-lg font-semibold text-primary">
                {formatISODate(fromDate, "MM/dd/yyyy") ?? "N/A"}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500 uppercase mb-1">
                To Date
              </div>
              <div className="text-lg font-semibold text-primary">
                {formatISODate(toDate, "MM/dd/yyyy") ?? "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
          <h2 className="text-lg font-semibold text-primary">Results List</h2>
        </div>
        <div className="p-2">
          {isLoading ? (
            <div className="mx-auto my-12 flex justify-center items-center gap-3">
              <Loader className="border-secondary" />
              <span className="text-gray-500 font-medium">
                Loading results data...
              </span>
            </div>
          ) : isError ? (
            <div className="mt-4 px-4 pb-4">
              <ApiError axiosError={error} />
            </div>
          ) : data && data.length > 0 ? (
            <StatisticsTable
              columns={resultTableColumns}
              data={data || []}
              loading={false}
              showTotalBar={false}
              sorting={sortingForResults}
              onSortingChange={setSortingForResults}
            />
          ) : (
            <div className="mx-auto my-12 text-center">
              <span className="material-icons text-4xl text-gray-400 mb-2">
                description_off
              </span>
              <p className="text-gray-500">
                No results found for the selected criteria.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResultsStatistics
